import { posthog } from 'posthog-js';
import {
    trackEvent as sharedTrackEvent,
    identifyUser as sharedIdentifyUser,
} from '../../../shared/utils/trackingService.js';

/**
 * ----------------------------------------------------------------
 * 🎨 Custom Mosaics Tracking Service
 * ----------------------------------------------------------------
 * Every event is sent to both the GTM Data Layer and PostHog and carries
 * `tool: 'custom_mosaics'` so it can be separated from the Mosaic Visualizer,
 * which shares some event names.
 */

const TOOL = 'custom_mosaics';

function track(eventName, properties = {}) {
    sharedTrackEvent(eventName, { tool: TOOL, ...properties });
}

export const identifyUser = sharedIdentifyUser;

/**
 * @param {Object} pattern - Pattern definition
 */
export const trackPatternSelection = (pattern) => {
    track('select_pattern', {
        pattern_id: pattern?.id,
        pattern_name: pattern?.name,
    });
};

/**
 * @param {string} colorName
 * @param {string} colorHex
 * @param {'swatches'|'wheel'} [pickerView] - Which picker view was used
 */
export const trackColorSelection = (colorName, colorHex, pickerView) => {
    track('select_paint_color', {
        color_name: colorName,
        color_hex: colorHex,
        picker_view: pickerView,
    });
};

/**
 * @param {Object} grout - Grout color record `{ id, code, name, hex }`
 */
export const trackGroutSelection = (grout) => {
    track('select_grout_color', {
        grout_id: grout?.id,
        grout_code: grout?.code,
        grout_name: grout?.name,
    });
};

/**
 * Fired once per paint stroke (not per tile) to keep volume reasonable.
 * @param {string} patternId
 * @param {'tile'|'fill'|'size'} mode
 * @param {number} tileCount - Tiles changed by the stroke
 * @param {string} colorName
 */
export const trackPaint = (patternId, mode, tileCount, colorName) => {
    track('paint_tiles', {
        pattern_id: patternId,
        paint_mode: mode,
        tile_count: tileCount,
        color_name: colorName,
    });
};

/**
 * @param {string} patternId
 */
export const trackReset = (patternId) => {
    track('reset_design', { pattern_id: patternId });
};

/**
 * @param {number} repeat - Copies per side
 */
export const trackRepeatChange = (repeat) => {
    track('adjust_layout_repeat', { repeat_count: repeat });
};

/**
 * "Schedule a call" CTA in the preview banner.
 * @param {string} patternId
 * @param {number} paintedCount
 */
export const trackConsultationClick = (patternId, paintedCount) => {
    track('click_schedule_consultation', {
        pattern_id: patternId,
        painted_count: paintedCount,
    });
};

/**
 * "Download Your Custom Specs" (PDF) in the preview banner.
 * @param {string} patternId
 * @param {number} paintedCount
 */
export const trackSpecsDownload = (patternId, paintedCount) => {
    track('download_specs_pdf', { pattern_id: patternId, painted_count: paintedCount });
};

const getShareUrlProperties = (params) => ({
    shared_pattern: params.get('p'),
    shared_grout: params.get('g'),
    shared_palette: params.get('pal'),
    shared_tiles: params.get('t') ? true : undefined,
});

/**
 * @param {string} shareUrl
 */
export const trackShareClick = (shareUrl) => {
    try {
        const url = new URL(shareUrl);
        track('click_share', { ...getShareUrlProperties(url.searchParams), share_url: shareUrl });
    } catch {
        track('click_share', { share_url: shareUrl, parse_error: true });
    }
};

/**
 * Tracks arrival from a shared link and tags the session so later events can
 * be attributed to it. Must run before the design params are stripped.
 */
export const trackViewSharedDesign = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('src') === 'share') {
            track('view_shared_design', getShareUrlProperties(params));
            posthog.register({ design_source: 'shared_link' });
        } else {
            posthog.unregister('design_source');
        }
    } catch (error) {
        console.error('Tracking Error: Could not parse entry URL.', error);
    }
};
